"use client";

import type { GamePrioritiesResponse } from "@/app/_lib/apis/gamePriorities";
import { QuestFilterPanel } from "@/components/QuestFilter/QuestFilterPanel";
import {
  QuestFilterProvider,
  useQuestFilter,
} from "@/components/QuestFilter/QuestFilterProvider";
import { QuestFilterResults } from "@/components/QuestFilter/QuestFilterResults";
import type { GameWithQuests } from "@/components/QuestsV2/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { Filter, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

function MobileFilterAccordion() {
  return (
    <div className="lg:hidden w-full shrink-0 border-b border-white/10">
      <Accordion type="single" collapsible>
        <AccordionItem value="filters" className="border-b-0">
          <AccordionTrigger className="px-4 py-3 text-sm font-medium text-white/70 hover:text-white">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-0 pb-0">
            <QuestFilterPanel variant="compact" />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

/**
 * Search header with input - uses QuestFilter context
 */
function SearchHeader({ onClose }: { onClose: () => void }) {
  const { filters, setFilters, totalQuestCount } = useQuestFilter();
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus search input when dialog opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearchChange = (value: string) => {
    setFilters((prev) => ({ ...prev, gameTitle: value }));
  };

  const handleClearSearch = () => {
    setFilters((prev) => ({ ...prev, gameTitle: "" }));
    inputRef.current?.focus();
  };

  return (
    <div className="flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-white/10">
      <Search className="w-5 h-5 text-white/40" />
      <Input
        ref={inputRef}
        placeholder="Search games and quests..."
        value={filters.gameTitle}
        onChange={(e) => handleSearchChange(e.target.value)}
        className="flex-1 border-0 bg-transparent text-white placeholder:text-white/40 focus-visible:ring-0 text-base px-0"
      />
      {filters.gameTitle && (
        <button
          type="button"
          onClick={handleClearSearch}
          className="text-white/40 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className="text-white/40 hover:text-white transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      <kbd className="hidden sm:flex items-center gap-1 px-2 py-0.5 text-xs bg-white/10 rounded border border-white/10 text-white/50">
        esc
      </kbd>
    </div>
  );
}

/**
 * Search dialog that wraps QuestFilter in a modal
 * Triggered from the QuestSearchBanner
 */
export function GameQuestSearchDialog({
  gamesWithQuests,
  gamePriorities,
}: {
  gamesWithQuests: GameWithQuests[];
  gamePriorities: GamePrioritiesResponse | null;
}) {
  const { trackV3 } = useAnalytics();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // "/" keyboard shortcut to open (in addition to CMD+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key === "/" || ((e.metaKey || e.ctrlKey) && e.key === "k")) {
        e.preventDefault();
        setIsSearchOpen(true);
        trackV3({
          object: "Search_Dialog",
          action: Action.OPENED,
          extras: {
            trigger: e.key === "/" ? "slash_shortcut" : "keyboard_shortcut",
          },
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [trackV3]);

  // Track when search dialog opens/closes
  const handleOpenChange = (open: boolean) => {
    setIsSearchOpen(open);
    if (open) {
      trackV3({
        object: "Search_Dialog",
        action: Action.OPENED,
        extras: { trigger: "button_click" },
      });
    } else {
      trackV3({
        object: "Search_Dialog",
        action: Action.CLOSED,
        extras: {},
      });
    }
  };

  return (
    <Dialog open={isSearchOpen} onOpenChange={handleOpenChange}>
      {/* Compact trigger button for banner */}
      <DialogTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/20 hover:border-white/30 rounded-lg text-white/70 hover:text-white transition-all duration-200 min-w-[240px] md:min-w-[300px]"
        >
          <Search className="w-4 h-4 text-white/50" />
          <span className="flex-1 text-left text-sm">
            Find quests and games
          </span>
          <kbd className="hidden sm:flex items-center justify-center w-5 h-5 text-xs text-white/50 bg-white/10 rounded border border-white/20">
            /
          </kbd>
        </button>
      </DialogTrigger>

      <DialogContent
        className="max-w-7xl w-full sm:w-[95vw] h-[100dvh] sm:h-[80vh] p-0 bg-neutral-950/95 backdrop-blur-xl border-0 sm:border sm:border-white/10 overflow-hidden rounded-none sm:rounded-lg z-[5]"
        overlayClassName="z-[5]"
        removeCloseButton
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Search games and quests</DialogTitle>

        <QuestFilterProvider
          gamesWithQuests={gamesWithQuests}
          gamePriorities={gamePriorities}
        >
          <div className="flex flex-col h-full overflow-hidden">
            {/* Search Header */}
            <div className="shrink-0">
              <SearchHeader onClose={() => setIsSearchOpen(false)} />
            </div>

            {/* Content: Filters + Results */}
            <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
              {/* Filter Panel - Collapsible accordion on mobile */}
              <MobileFilterAccordion />
              {/* Desktop sidebar */}
              <div className="hidden lg:block lg:w-64 shrink-0 overflow-y-auto border-r border-white/10">
                <QuestFilterPanel variant="compact" />
              </div>

              {/* Results */}
              <div className="flex-1 min-h-0 p-4 lg:p-6 overflow-y-auto">
                <QuestFilterResults variant="compact" />
              </div>
            </div>
          </div>
        </QuestFilterProvider>
      </DialogContent>
    </Dialog>
  );
}
